import type { ButtonProps } from '@imprivata-cloud/components';
import { Flex, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { dirSyncProgress, findDirectories } from '../../../api/portalServices';
import type { Directory, SyncStatus } from '../../../api/types';
import MenuIcon from '../../../assets/icons/kabob_menu.svg?react';
import { useSession } from '../../../common/authorization/context/Session';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import type { AppError } from '../../../errorHandler/errors';
import UsersEmptyPage from './UsersEmptyPage';
import GroupsModal from './groups/GroupsModal';
import useInfiniteScroll from './hooks/useInfiniteScroll';
import ImprListView from './impr-list-view/ImprListView';
import Sync from './sync/Sync';

const Users = () => {
  const { tenantId } = useSession();
  const [directories, setDirectories] = useState<Directory[]>();
  const { emitError } = useErrorContext();
  const [initSyncStatus, setInitSyncStatus] = useState<SyncStatus>();
  const lastElementRef = useRef<HTMLDivElement | null>(null);
  const [searchVal, setSearchVal] = useState<string>('');

  const {
    data: userList = [],
    isFetchingNextPage,
    refetch,
    isLoading,
  } = useInfiniteScroll({
    lastElementRef,
    searchTerm: searchVal,
  });

  const directoriesLoaded = useRef(false);

  const handleSearch = () => {
    refetch();
  };

  const handleSearchChange = (searchValue: string) => {
    setSearchVal(searchValue);
  };

  const loadUsers = useCallback(async () => {
    console.log('[Users] loadUsers');
    refetch();
  }, [refetch]);

  useEffect(() => {
    const fetchDirectories = async () => {
      try {
        const response = await findDirectories(tenantId ?? '');
        const directories = response?.directories;
        if (directories && directories[0]?.syncedGroupCount === 0) {
          try {
            const resp = await dirSyncProgress(tenantId || '', directories[0]?.directoryId);
            setInitSyncStatus(resp?.syncStatus);
          } catch (err) {
            emitError(err as AppError);
          }
        }
        setDirectories(directories);
      } catch (err) {
        emitError(err as AppError);
      }
    };
    if (!directoriesLoaded.current) {
      directoriesLoaded.current = true;
      void fetchDirectories();
    }
  }, [tenantId, emitError]);

  const groupsButtonProps: ButtonProps = {
    icon: <MenuIcon />,
    size: 'small',
    style: { borderColor: 'transparent', boxShadow: 'none', marginLeft: 'auto' },
  };

  if (isLoading) {
    return (
      <Flex justify={'center'} style={{ padding: 15 }}>
        <Spin data-testid={'loading-indicator'} size="small" />
      </Flex>
    );
  }

  return (
    <div>
      <ImprListView
        enableMenu={true}
        menuIcon={<MenuIcon />}
        dataSource={userList}
        customEmptyListNode={<UsersEmptyPage directories={directories} initSyncStatus={initSyncStatus} />}
        sortLabel={'All users (by name)'}
        handleSearch={handleSearch}
        onSearchChange={handleSearchChange}
        rowKey={'upn'}
        loading={isLoading}
        headerExtraComponents={[
          <>
            {directories?.[0]?.directoryId ? (
              <>
                <Sync
                  key={'manualSync'}
                  tenantId={tenantId}
                  directoryId={directories?.[0]?.directoryId}
                  loadUsers={loadUsers}
                />
                <GroupsModal
                  groupsButtonProps={groupsButtonProps}
                  initSyncStatus={initSyncStatus}
                  tenantId={tenantId || ''}
                  key={'groupsMenu'}
                />
              </>
            ) : (
              <div key={'emptyUserMenu'} />
            )}
          </>,
        ]}
      />
      <div ref={lastElementRef} data-testid="user-list-last-element">
        {isFetchingNextPage && (
          <Flex justify={'center'} style={{ padding: 15 }}>
            <Spin data-testid={'loading-indicator'} size="small" />
          </Flex>
        )}
      </div>
    </div>
  );
};

export default Users;
